import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, CheckCircle, Play, Pause, Compass, HeartStraight, ChatCircleDots, MapTrifold,
  Brain, Briefcase, GraduationCap, ArrowUUpLeft, UserCircleGear, PaperPlaneTilt, Phone as PhoneIcon, Lightbulb,
} from "@phosphor-icons/react";
import { useSeo } from "../lib/useSeo";
import { CMH_NAME } from "../lib/constants";
import { useLang } from "../lib/i18n";
import { useAutoPlay } from "../lib/useAutoPlay";
import { submitContact } from "../lib/api";
import { Reveal } from "../components/blocks";
import { CmhHeroVisual, CmhIntentVisual } from "../components/replicas/CmhReplicas";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STR = {
  en: {
    soon: "Launching soon",
    h1a: "Clarity for your ", h1aAccent: "career.",
    h1b: " Care for your ", h1bAccent: "mind.",
    sub: "An AI-powered platform that combines psychometric science, AI guidance and real counsellors, to help you make important career and life decisions with confidence.",
    ctaJoin: "Join the early list",
    ctaHow: "How it will work",

    whoEyebrow: "Who we help",
    whoTitle: "Built for India's ", whoAccent: "workforce.",
    who: [
      { icon: Briefcase, t: "IT professionals", items: ["Career transitions", "Layoffs", "AI uncertainty", "Job switching"] },
      { icon: GraduationCap, t: "Exam aspirants", items: ["UPSC · PCS · SSC", "Banking · Railway", "CDS · CAPF", "Continue-or-pivot calls"] },
      { icon: ArrowUUpLeft, t: "Career returnees", items: ["Career break", "Restart", "Maternity return", "Confidence rebuilding"] },
      { icon: Compass, t: "Working professionals", items: ["Promotion", "Upskilling", "MBA decisions", "Career planning"] },
    ],
    alsoFor: "Also supporting",
    alsoChips: ["MBA aspirants & graduates", "Legal professionals", "Doctors & healthcare", "CA / finance professionals", "Students choosing careers", "Entrepreneurs"],

    stuckTitle: "Feeling stuck? ", stuckAccent: "You're not alone.",
    stuckSub: "These are the questions we help people answer every day — with structure, not guesswork.",
    stuck: [
      "Should I continue UPSC preparation?",
      "Should I switch jobs?",
      "Should I resign?",
      "Am I wasting my time?",
      "Should I learn AI?",
      "Should I pursue higher education?",
      "How long should I keep trying?",
      "What is my backup plan?",
    ],

    howEyebrow: "How it will work",
    howTitle: "Four steps, from ", howAccent: "question to plan.",
    play: "Play", pause: "Pause",
    steps: [
      { icon: ChatCircleDots, t: "Free AI check-in", d: "3–5 minutes. An adaptive interview, or a quick mode if you are short on time.", b: ["No forms to fill — it asks, you answer.", "Your answers stay private to you."] },
      { icon: Brain, t: "Your AI Career Snapshot", d: "A clear read of where you are, what fits you and what is holding you back.", b: ["Built on psychometric science, not opinions.", "Written in plain language you can act on."] },
      { icon: HeartStraight, t: "Talk to a real mentor or counsellor", d: "Choose a mentor for career decisions, or a licensed counsellor for how you are feeling.", b: ["Sessions happen inside the platform.", "You decide what you share."] },
      { icon: MapTrifold, t: "Your roadmap and ongoing support", d: "A step-by-step plan, with check-ins as things change.", b: ["Revisit the plan whenever your situation shifts.", "Support continues after the first session."] },
    ],

    pillarsEyebrow: "Two pillars",
    pillarsTitle: "Career and mind, ", pillarsAccent: "in one place.",
    pillarCareer: "Career guidance",
    pillarCareerItems: ["Assessments", "Personalised roadmaps", "Expert counselling", "Resume help", "Mock interviews", "Study strategies", "Mentorship"],
    pillarMind: "Mental well-being",
    pillarMindItems: ["Stress", "Anxiety", "Low mood", "Relationships", "Marriage and family", "One-to-one counselling with licensed counsellors"],

    proEyebrow: "For professionals",
    proTitle: "Mentors and counsellors: ", proAccent: "join us before launch.",
    pro: [
      { t: "Become a mentor", d: "Guide people through the decisions you have already made." },
      { t: "Become a counsellor", d: "Practise inside a platform built for safe, structured sessions." },
      { t: "Earn on your schedule", d: "Take the sessions that fit around your work." },
    ],
    proCta: "Apply to join",

    formEyebrow: "Early list",
    formTitle: "Be first to know ", formAccent: "when we launch.",
    formSub: "Leave your details and we will message you when Atikxzio CMH opens.",
    fName: "Name", fMobile: "Mobile", fEmail: "Email", fRole: "I am a", fHelp: "What would help most (optional)",
    rolePh: "Select one",
    roles: ["Student", "Job seeker", "Working professional", "Exam aspirant", "Mentor or counsellor"],
    helpPh: "Tell us in a line or two, if you like.",
    send: "Join the early list", sending: "Sending...",
    okTitle: "You're on the list.",
    okSub: "We'll message you when we launch.",
    errFill: "Please add your name, mobile and email.",
    errSend: "Could not send just now. Please try again.",

    care: "Atikxzio CMH is guidance and counselling, not emergency or psychiatric care. If you are in crisis, call Tele-MANAS 14416 (free, 24×7) or go to the nearest hospital.",
  },
  hi: {
    soon: "जल्द लॉन्च",
    h1a: "करियर में ", h1aAccent: "स्पष्टता।",
    h1b: " मन की ", h1bAccent: "देखभाल।",
    sub: "एक AI-आधारित प्लेटफ़ॉर्म जो साइकोमेट्रिक विज्ञान, AI मार्गदर्शन और असली काउंसलर्स को जोड़ता है — ताकि आप करियर और जीवन के अहम फ़ैसले आत्मविश्वास से ले सकें।",
    ctaJoin: "अर्ली लिस्ट में जुड़ें",
    ctaHow: "यह कैसे काम करेगा",

    whoEyebrow: "हम किनकी मदद करते हैं",
    whoTitle: "भारत के ", whoAccent: "वर्कफ़ोर्स के लिए बना।",
    who: [
      { icon: Briefcase, t: "IT प्रोफेशनल", items: ["करियर बदलाव", "छँटनी", "AI की अनिश्चितता", "जॉब स्विच"] },
      { icon: GraduationCap, t: "परीक्षा अभ्यर्थी", items: ["UPSC · PCS · SSC", "बैंकिंग · रेलवे", "CDS · CAPF", "जारी रखें या बदलें"] },
      { icon: ArrowUUpLeft, t: "करियर में वापसी", items: ["करियर ब्रेक", "दोबारा शुरुआत", "मातृत्व के बाद वापसी", "आत्मविश्वास"] },
      { icon: Compass, t: "कामकाजी प्रोफेशनल", items: ["प्रमोशन", "अपस्किलिंग", "MBA के फ़ैसले", "करियर प्लानिंग"] },
    ],
    alsoFor: "इनका भी साथ",
    alsoChips: ["MBA अभ्यर्थी व ग्रेजुएट", "क़ानून पेशेवर", "डॉक्टर व हेल्थकेयर", "CA / फ़ाइनेंस पेशेवर", "करियर चुनते छात्र", "उद्यमी"],

    stuckTitle: "उलझन में हैं? ", stuckAccent: "आप अकेले नहीं हैं।",
    stuckSub: "यही सवाल हम रोज़ सुलझाने में मदद करते हैं — अंदाज़े से नहीं, एक व्यवस्था के साथ।",
    stuck: [
      "क्या मुझे UPSC की तैयारी जारी रखनी चाहिए?",
      "क्या मुझे नौकरी बदलनी चाहिए?",
      "क्या मुझे इस्तीफ़ा देना चाहिए?",
      "क्या मैं अपना समय बर्बाद कर रहा/रही हूँ?",
      "क्या मुझे AI सीखना चाहिए?",
      "क्या मुझे उच्च शिक्षा लेनी चाहिए?",
      "कब तक कोशिश करता/करती रहूँ?",
      "मेरा बैकअप प्लान क्या है?",
    ],

    howEyebrow: "यह कैसे काम करेगा",
    howTitle: "सवाल से योजना तक, ", howAccent: "चार क़दम।",
    play: "चलाएँ", pause: "रोकें",
    steps: [
      { icon: ChatCircleDots, t: "फ्री AI चेक-इन", d: "3–5 मिनट। एक अनुकूल इंटरव्यू, या समय कम हो तो क्विक मोड।", b: ["कोई फ़ॉर्म नहीं — यह पूछता है, आप जवाब देते हैं।", "आपके जवाब आपके ही रहते हैं।"] },
      { icon: Brain, t: "आपका AI करियर स्नैपशॉट", d: "आप कहाँ हैं, क्या आप पर फ़िट बैठता है और क्या रोक रहा है — साफ़ तस्वीर।", b: ["साइकोमेट्रिक विज्ञान पर आधारित, राय पर नहीं।", "आसान भाषा में, जिस पर क़दम उठाया जा सके।"] },
      { icon: HeartStraight, t: "असली मेंटर या काउंसलर से बात", d: "करियर के फ़ैसलों के लिए मेंटर, और मन की बात के लिए लाइसेंस प्राप्त काउंसलर चुनिए।", b: ["सेशन प्लेटफ़ॉर्म के भीतर ही होते हैं।", "क्या साझा करना है, यह आप तय करते हैं।"] },
      { icon: MapTrifold, t: "आपका रोडमैप और निरंतर सहयोग", d: "चरण-दर-चरण योजना, और हालात बदलने पर चेक-इन।", b: ["स्थिति बदले तो योजना दोबारा देखिए।", "पहले सेशन के बाद भी सहयोग जारी रहता है।"] },
    ],

    pillarsEyebrow: "दो स्तंभ",
    pillarsTitle: "करियर और मन, ", pillarsAccent: "एक ही जगह।",
    pillarCareer: "करियर मार्गदर्शन",
    pillarCareerItems: ["असेसमेंट", "व्यक्तिगत रोडमैप", "विशेषज्ञ काउंसलिंग", "रिज़्यूमे में मदद", "मॉक इंटरव्यू", "पढ़ाई की रणनीति", "मेंटरशिप"],
    pillarMind: "मानसिक स्वास्थ्य",
    pillarMindItems: ["तनाव", "चिंता", "उदासी", "रिश्ते", "विवाह व परिवार", "लाइसेंस प्राप्त काउंसलर्स के साथ वन-टू-वन काउंसलिंग"],

    proEyebrow: "प्रोफेशनल्स के लिए",
    proTitle: "मेंटर और काउंसलर: ", proAccent: "लॉन्च से पहले जुड़िए।",
    pro: [
      { t: "मेंटर बनिए", d: "जो फ़ैसले आप ले चुके हैं, उनमें दूसरों का मार्गदर्शन कीजिए।" },
      { t: "काउंसलर बनिए", d: "सुरक्षित, संरचित सेशन के लिए बने प्लेटफ़ॉर्म पर प्रैक्टिस कीजिए।" },
      { t: "अपने समय पर कमाइए", d: "वही सेशन लीजिए जो आपके काम के साथ फ़िट बैठें।" },
    ],
    proCta: "जुड़ने के लिए आवेदन करें",

    formEyebrow: "अर्ली लिस्ट",
    formTitle: "लॉन्च होते ही ", formAccent: "सबसे पहले जानिए।",
    formSub: "अपनी जानकारी छोड़िए, Atikxzio CMH शुरू होते ही हम आपको संदेश भेजेंगे।",
    fName: "नाम", fMobile: "मोबाइल", fEmail: "ईमेल", fRole: "मैं हूँ", fHelp: "सबसे ज़्यादा किसमें मदद चाहिए (वैकल्पिक)",
    rolePh: "एक चुनें",
    roles: ["छात्र", "नौकरी खोज रहे हैं", "कामकाजी प्रोफेशनल", "परीक्षा अभ्यर्थी", "मेंटर या काउंसलर"],
    helpPh: "चाहें तो एक-दो पंक्ति में बताइए।",
    send: "अर्ली लिस्ट में जुड़ें", sending: "भेजा जा रहा है...",
    okTitle: "आप लिस्ट में हैं।",
    okSub: "लॉन्च होते ही हम आपको संदेश भेजेंगे।",
    errFill: "कृपया नाम, मोबाइल और ईमेल भरें।",
    errSend: "अभी भेजा नहीं जा सका। कृपया दोबारा कोशिश करें।",

    care: "Atikxzio CMH मार्गदर्शन और काउंसलिंग है — आपातकालीन या मनोरोग चिकित्सा नहीं। संकट की स्थिति में Tele-MANAS 14416 (नि:शुल्क, 24×7) पर कॉल करें या नज़दीकी अस्पताल जाएँ।",
  },
};

const Eyebrow = ({ children, tone = "teal" }) => (
  <p className={`text-xs font-bold uppercase tracking-[0.22em] ${tone === "light" ? "text-[#8FC9BF]" : "text-[#2E7D74]"}`}>{children}</p>
);

/* How it will work — 4 auto-playing steps. */
const HowItWorks = ({ s }) => {
  const { ref, active, playing, pick, toggle, reduced, mounted } = useAutoPlay(s.steps.length, { first: 3000, every: 3500, hold: 8000 });

  const step = s.steps[active];

  return (
    <div ref={ref} data-testid="cmh-how">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow tone="light">{s.howEyebrow}</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-[1.75rem] font-bold leading-tight tracking-[-0.02em] text-white lg:text-[2.5rem]">{s.howTitle}<span className="italic text-[#8FC9BF]">{s.howAccent}</span></h2>
        </div>
        {!reduced && (
          <button
            onClick={toggle}
            aria-label={playing ? s.pause : s.play}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-200 hover:bg-white/10"
            data-testid="cmh-how-playpause"
          >
            {playing ? <Pause size={15} weight="fill" /> : <Play size={15} weight="fill" />}
          </button>
        )}
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-12">
        <ol className="lg:col-span-5">
          {s.steps.map((st, i) => (
            <li key={st.t}>
              <button
                onClick={() => pick(i)}
                aria-pressed={active === i}
                className={`flex w-full items-start gap-4 border-l-2 py-4 pl-5 text-left transition-colors duration-300 ${
                  active === i ? "border-[#8FC9BF]" : "border-white/12 hover:border-white/30"
                }`}
                data-testid={`cmh-step-${i}`}
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-300 ${
                  active === i ? "bg-[#2E7D74] text-white" : "bg-white/[0.07] text-white/60"
                }`}>
                  <st.icon size={18} weight="duotone" />
                </span>
                <span>
                  <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-white/45">0{i + 1}</span>
                  <span className={`block font-display text-base font-bold ${active === i ? "text-white" : "text-white/70"}`}>{st.t}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>

        <div className="lg:col-span-7">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step.t}
              initial={mounted ? { opacity: 0, y: 14 } : false}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
              className="rounded-[24px] border border-white/10 bg-white/[0.05] p-7 backdrop-blur-md"
              data-testid={`cmh-step-detail-${active}`}
            >
              <h3 className="font-display text-xl font-bold text-white">{step.t}</h3>
              <p className="mt-3 text-[17px] leading-relaxed text-slate-300">{step.d}</p>
              <ul className="mt-5 space-y-3">
                {step.b.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-300">
                    <CheckCircle size={17} weight="fill" className="mt-0.5 shrink-0 text-[#8FC9BF]" /> {b}
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

/* Early list form — lands in the same inbox as demo requests. */
const EarlyListForm = ({ s }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", role: "", help: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error(s.errFill);
      return;
    }
    setLoading(true);
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: "",
        product_interest: CMH_NAME,
        message: `${s.fRole}: ${form.role || "—"}\n${s.fHelp}: ${form.help || "—"}`,
      });
      setDone(true);
      toast.success(s.okTitle);
    } catch {
      toast.error(s.errSend);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-start rounded-[24px] border border-white/10 bg-white/[0.05] p-8 backdrop-blur-md" data-testid="cmh-form-success">
        <CheckCircle size={44} weight="fill" className="text-[#8FC9BF]" />
        <h3 className="mt-4 font-display text-xl font-bold text-white">{s.okTitle}</h3>
        <p className="mt-2 text-[15px] text-slate-300">{s.okSub}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-[24px] border border-white/10 bg-white/[0.05] p-7 backdrop-blur-md sm:p-8" data-testid="cmh-early-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <Label className="mb-1.5 block text-sm font-bold text-white">{s.fName}</Label>
          <Input value={form.name} onChange={set("name")} className="h-12 rounded-xl border-white/25 bg-white/[0.13] text-white placeholder:text-white/65" data-testid="cmh-input-name" />
        </div>
        <div>
          <Label className="mb-1.5 block text-sm font-bold text-white">{s.fMobile}</Label>
          <Input value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="h-12 rounded-xl border-white/25 bg-white/[0.13] text-white placeholder:text-white/65" data-testid="cmh-input-phone" />
        </div>
        <div>
          <Label className="mb-1.5 block text-sm font-bold text-white">{s.fEmail}</Label>
          <Input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="h-12 rounded-xl border-white/25 bg-white/[0.13] text-white placeholder:text-white/65" data-testid="cmh-input-email" />
        </div>
        <div>
          <Label className="mb-1.5 block text-sm font-bold text-white">{s.fRole}</Label>
          <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
            <SelectTrigger className="h-12 rounded-xl border-white/25 bg-white/[0.13] text-white" data-testid="cmh-select-role"><SelectValue placeholder={s.rolePh} /></SelectTrigger>
            <SelectContent>
              {s.roles.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-5">
        <Label className="mb-1.5 block text-sm font-bold text-white">{s.fHelp}</Label>
        <Textarea value={form.help} onChange={set("help")} rows={3} placeholder={s.helpPh} className="rounded-xl border-white/25 bg-white/[0.13] text-white placeholder:text-white/65" data-testid="cmh-input-help" />
      </div>
      <button type="submit" disabled={loading} className="mt-6 inline-flex w-full items-center justify-center gap-2 cmh-btn-primary rounded-full px-7 py-4 text-sm font-bold transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto" data-testid="cmh-form-submit">
        {loading ? s.sending : <>{s.send} <PaperPlaneTilt size={17} weight="fill" /></>}
      </button>
    </form>
  );
};

export default function ProductCMH() {
  const { lang } = useLang();
  const s = STR[lang];

  useSeo({
    title: "Atikxzio CMH — Career guidance & mental well-being | Launching soon",
    description: "Atikxzio CMH combines psychometric science, AI guidance and real counsellors to help you make career and life decisions with confidence. Launching soon from Priyajit Systems.",
    image: "/og-image.jpg",
    keywords: "career guidance India, career counselling, mental well-being platform, UPSC continue or pivot, Atikxzio CMH",
  });

  return (
    <div className="theme-cmh font-display">
      {/* 1 · Hero */}
      <section className="relative overflow-hidden bg-[#F8F6F3] pt-32 lg:pt-40" data-testid="cmh-hero">
        <div aria-hidden className="absolute -left-32 top-20 h-[28rem] w-[28rem] rounded-full bg-[#2E7D74]/10 blur-[120px]" />
        <div aria-hidden className="absolute -right-20 top-40 h-[22rem] w-[22rem] rounded-full bg-[#C97B5E]/10 blur-[110px]" />
        <div className="relative mx-auto grid max-w-[1200px] items-center gap-14 px-5 pb-20 lg:grid-cols-12 lg:px-8 lg:pb-28">
          <div className="lg:col-span-6">
            <span className="cmh-badge inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em]" data-testid="cmh-hero-badge">
              {s.soon}
            </span>
            <h1 className="mt-7 font-display text-[2.25rem] font-black leading-[1.1] tracking-[-0.02em] text-[#1D4B57] sm:text-5xl lg:text-[3.5rem]" data-testid="cmh-h1">
              <span className="block">{s.h1a}<span className="cmh-accent">{s.h1aAccent}</span></span><span className="block">{s.h1b.trim()} <span className="cmh-accent">{s.h1bAccent}</span></span>
            </h1>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-[#4A5A60] sm:text-[17px]">{s.sub}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#early-list" className="group inline-flex items-center gap-2 cmh-btn-primary rounded-full px-7 py-3.5 text-sm font-bold transition-transform duration-200 hover:-translate-y-0.5" data-testid="cmh-hero-join">
                {s.ctaJoin} <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </a>
              <a href="#how-it-works" className="cmh-btn-outline inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold transition-colors duration-200" data-testid="cmh-hero-how">
                {s.ctaHow}
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="cmh-card p-3 shadow-[0_24px_60px_rgba(11,79,67,0.12)]">
              <CmhHeroVisual lang={lang} />
            </div>
          </div>
        </div>
      </section>

      {/* 2 · Who it's for */}
      <section className="bg-white py-20 lg:py-28" data-testid="cmh-who">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <Reveal>
            <Eyebrow>{s.whoEyebrow}</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-display text-[1.75rem] font-bold leading-tight tracking-[-0.02em] text-[#1D4B57] lg:text-[2.5rem]">{s.whoTitle}<span className="cmh-accent">{s.whoAccent}</span></h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {s.who.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.07}>
                <div className="h-full cmh-card p-6" data-testid={`cmh-who-${i}`}>
                  <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${i % 2 ? "cmh-tile-clay" : "cmh-tile-mist"}`}><w.icon size={23} weight="duotone" /></span>
                  <h3 className="mt-5 font-display text-base font-bold text-[#1D4B57]">{w.t}</h3>
                  <ul className="mt-3 space-y-1.5">
                    {w.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-[15px] leading-snug text-[#4A5A60]">
                        <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[#2E7D74]" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap items-center gap-2.5">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#A35A3E]">{s.alsoFor}</span>
              {s.alsoChips.map((c) => (
                <span key={c} className="rounded-full border border-[#C97B5E]/25 bg-[#C97B5E]/[0.07] px-3.5 py-1.5 text-xs font-semibold text-[#A35A3E]" data-testid={`cmh-also-${c}`}>{c}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3 · Feeling stuck */}
      <section className="cmh-bg py-20 lg:py-28" data-testid="cmh-stuck">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <Reveal>
            <h2 className="max-w-2xl font-display text-[1.75rem] font-bold leading-tight tracking-[-0.02em] text-[#1D4B57] lg:text-[2.5rem]">{s.stuckTitle}<span className="cmh-accent">{s.stuckAccent}</span></h2>
            <p className="mt-4 text-[17px] text-[#4A5A60]">{s.stuckSub}</p>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {s.stuck.map((q, i) => (
              <Reveal key={q} delay={(i % 4) * 0.06}>
                <div className="flex h-full items-start gap-3 cmh-card p-5" data-testid={`cmh-stuck-${i}`}>
                  <Lightbulb size={18} weight="duotone" className="mt-0.5 shrink-0 text-[#C97B5E]" />
                  <p className="text-[15px] font-semibold leading-snug text-[#1D4B57]">{q}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · How it will work */}
      <section id="how-it-works" className="scroll-mt-24 cmh-dark relative overflow-hidden py-20 lg:py-28" data-testid="cmh-how-section">
        <div aria-hidden className="absolute -left-24 top-0 h-[26rem] w-[26rem] rounded-full bg-[#2E7D74]/35 blur-[130px]" />
        <div className="relative mx-auto max-w-[1200px] px-5 lg:px-8">
          <HowItWorks s={s} />
        </div>
      </section>

      {/* 5 · Two pillars */}
      <section className="bg-white py-20 lg:py-28" data-testid="cmh-pillars">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <Reveal>
            <Eyebrow>{s.pillarsEyebrow}</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-display text-[1.75rem] font-bold leading-tight tracking-[-0.02em] text-[#1D4B57] lg:text-[2.5rem]">{s.pillarsTitle}<span className="cmh-accent">{s.pillarsAccent}</span></h2>
          </Reveal>

          <div className="mt-12 grid items-stretch gap-8 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <div className="h-full rounded-[24px] border border-[#2E7D74]/20 bg-[#FFFFFF] p-7" data-testid="cmh-pillar-career">
                <span className="cmh-tile-mist flex h-12 w-12 items-center justify-center rounded-xl"><Compass size={23} weight="duotone" /></span>
                <h3 className="mt-5 font-display text-lg font-bold text-[#1D4B57]">{s.pillarCareer}</h3>
                <ul className="mt-5 space-y-2.5">
                  {s.pillarCareerItems.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-[15px] text-[#4A5A60]">
                      <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-[#2E7D74]" /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.08} className="lg:col-span-4 lg:self-center">
              <div className="flex justify-center"><CmhIntentVisual lang={lang} /></div>
            </Reveal>

            <Reveal delay={0.16} className="lg:col-span-4">
              <div className="h-full rounded-[24px] border border-[#C97B5E]/25 bg-[#F8F6F3] p-7" data-testid="cmh-pillar-mind">
                <span className="cmh-tile-clay flex h-12 w-12 items-center justify-center rounded-xl"><HeartStraight size={23} weight="duotone" /></span>
                <h3 className="mt-5 font-display text-lg font-bold text-[#1D4B57]">{s.pillarMind}</h3>
                <ul className="mt-5 space-y-2.5">
                  {s.pillarMindItems.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-[15px] text-[#4A5A60]">
                      <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-[#C97B5E]" /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 6 · For professionals */}
      <section className="cmh-bg py-20 lg:py-28" data-testid="cmh-professionals">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <Reveal>
            <Eyebrow>{s.proEyebrow}</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-display text-[1.75rem] font-bold leading-tight tracking-[-0.02em] text-[#1D4B57] lg:text-[2.5rem]">{s.proTitle}<span className="cmh-accent">{s.proAccent}</span></h2>
          </Reveal>
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {s.pro.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.07}>
                <div className="h-full cmh-card p-6" data-testid={`cmh-pro-${i}`}>
                  <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${i === 1 ? "cmh-tile-clay" : "cmh-tile-mist"}`}><UserCircleGear size={22} weight="duotone" /></span>
                  <h3 className="mt-5 font-display text-base font-bold text-[#1D4B57]">{p.t}</h3>
                  <p className="mt-2.5 text-[15px] leading-relaxed text-[#4A5A60]">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Link to="/contact?interest=cmh-professional" className="group mt-10 inline-flex items-center gap-2 cmh-btn-primary rounded-full px-7 py-3.5 text-sm font-bold transition-transform duration-200 hover:-translate-y-0.5" data-testid="cmh-pro-cta">
            {s.proCta} <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* 7 · Early list */}
      <section id="early-list" className="scroll-mt-24 cmh-dark relative overflow-hidden py-20 lg:py-28" data-testid="cmh-early-list">
        <div aria-hidden className="absolute -right-20 bottom-0 h-[24rem] w-[24rem] rounded-full bg-[#2E7D74]/30 blur-[130px]" />
        <div className="relative mx-auto grid max-w-[1200px] gap-10 px-5 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <Eyebrow tone="light">{s.formEyebrow}</Eyebrow>
            <h2 className="mt-4 font-display text-[1.75rem] font-bold leading-tight tracking-[-0.02em] text-white lg:text-[2.5rem]">{s.formTitle}<span className="italic text-[#8FC9BF]">{s.formAccent}</span></h2>
            <p className="mt-4 text-[17px] leading-relaxed text-slate-300">{s.formSub}</p>
          </div>
          <div className="lg:col-span-7"><EarlyListForm s={s} /></div>
        </div>
      </section>

      {/* 8 · Care note */}
      <section className="bg-white py-10" data-testid="cmh-care-note">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-8">
          <p className="flex items-start gap-3 rounded-[24px] border border-[#C97B5E]/25 bg-[#F8F6F3] p-5 text-[13px] leading-relaxed text-[#4A5A60]">
            <PhoneIcon size={18} weight="duotone" className="mt-0.5 shrink-0 text-[#A35A3E]" />
            {s.care}
          </p>
        </div>
      </section>
    </div>
  );
}
